import { createSelector } from 'reselect';

import ggAuth from 'ggApp/modules/auth';
import ggProduct from 'ggApp/modules/product';
import ggOrder from 'ggApp/modules/order';
import ggCategory from 'ggApp/modules/category';
import { priceDecimalizer, md5Hasher } from 'ggApp/modules/criteo/adapters';

const buildHashedEmail = (email = '') => (email ? md5Hasher(email) : '');

const buildCrtoProductData = (email = '', productList = []) => ({
    email: buildHashedEmail(email),
    products: productList.map((product) =>
        product.variants && product.variants.length ? product.variants[0].id : null,
    ),
});

export const criteoHomePageTracking = createSelector(ggAuth.selectors.user, (user) => ({
    event: 'crto_homepage',
    list: 'homepage',
    crto: {
        email: buildHashedEmail(user.email),
    },
}));

export const criteoCategoryTracking = createSelector(
    ggAuth.selectors.user,
    ggCategory.selectors.topProducts,
    (user, productList) => {
        if (!productList) return null;
        const topThreeProducts = productList.products.slice(0, 3);
        return {
            event: 'crto_listingpage',
            list: 'category',
            crto: buildCrtoProductData(user.email, topThreeProducts),
        };
    },
);

export const criteoSubCategoryTracking = createSelector(
    ggAuth.selectors.user,
    ggProduct.selectors.list,
    (user, list) => {
        if (!list.data || !list.data.length) return null;
        const topThreeProducts = list.data.slice(0, 3);
        return {
            event: 'crto_listingpage',
            list: 'subcategory',
            crto: buildCrtoProductData(user.email, topThreeProducts),
        };
    },
);

export const criteoProductTracking = createSelector(
    ggAuth.selectors.user,
    ggProduct.selectors.currentProduct,
    (user, product) => {
        if (!product) return null;
        return {
            event: 'crto_productpage',
            list: 'pdp',
            crto: buildCrtoProductData(user.email, [product]),
        };
    },
);

export const criteoCartTracking = createSelector(
    ggAuth.selectors.user,
    ggOrder.selectors.orderActive,
    (user, order) => {
        if (!order) return null;
        return {
            event: 'crto_basketpage',
            list: 'basket',
            crto: {
                email: buildHashedEmail(user.email),
                products: (order.items || []).map((product) => ({
                    id: product.variant.id,
                    price: priceDecimalizer(product.price_cents),
                    quantity: product.quantity,
                })),
            },
        };
    },
);

export const criteoConfirmationOrderTracking = createSelector(
    ggAuth.selectors.user,
    ggOrder.selectors.orderActive,
    (user, order) => {
        if (!order) return null;
        return {
            event: 'crto_transactionpage',
            list: 'confirmation',
            crto: {
                email: buildHashedEmail(user.email),
                transactionid: order.number,
                products: (order.items || []).map((product) => ({
                    id: product.variant.id,
                    price: priceDecimalizer(product.price_cents),
                    quantity: product.quantity,
                })),
            },
        };
    },
);
